import { TICK_HZ, type Resources } from '@somnhold/content';
import { idiv } from '../math/fixed.ts';
import type { BattleState, PlayerId } from '../types.ts';
import type { CampaignState, DeedBest, DeedMode, DeedRow, FightDeeds, HeroDeeds, HeroWorks } from './types.ts';

/**
 * The hall of deeds: what each hero did in a fight (damage, healing, damage taken, kills, downs) and,
 * summed over the campaign, per kind of fight. Only the fights a hero played live count for the campaign;
 * a fight's own deeds list everyone in it, ghosts too.
 */

/** One fight's deeds, for every hero in it but mercenaries. */
export const fightDeeds = (b: BattleState): FightDeeds => {
  const by: Record<PlayerId, DeedRow> = {};
  for (const h of b.heroes) {
    if (h.control === 'merc') continue;
    const p = b.stats.players[h.pid];
    by[h.pid] = [p?.damage ?? 0, p?.healed ?? 0, p?.taken ?? 0, p?.kills ?? 0, p?.downs ?? 0, p?.aided ?? 0];
  }
  return { ticks: b.tick, by };
};

/** A figure per second of fighting (whole numbers, rounded down). */
export const perSecond = (n: number, ticks: number): number => (ticks > 0 ? idiv(n * TICK_HZ, ticks) : 0);

/** Fights shorter than this don't set per-second records (a few lucky blows would). */
const RECORD_MIN_TICKS = 20 * TICK_HZ;

const better = (old: DeedBest | undefined, n: number, mode: DeedMode, at: number): DeedBest | undefined => (n > 0 && (!old || n > old.n) ? { n, mode, at } : old);

/** Adds a finished fight to the campaign's deeds, for each hero that played it live. */
export const tallyDeeds = (s: CampaignState, mode: DeedMode, b: BattleState, now: number): void => {
  const fd = fightDeeds(b);
  for (const h of b.heroes) {
    if (h.control !== 'live' || !s.players.some((p) => p.id === h.pid)) continue;
    const row = fd.by[h.pid];
    if (!row) continue;
    const all = (s.deeds ??= {});
    const d: HeroDeeds = (all[h.pid] ??= { by: {} });
    const t = (d.by[mode] ??= { fights: 0, ticks: 0, row: [0, 0, 0, 0, 0, 0] });
    t.fights++;
    t.ticks += fd.ticks;
    t.row = [0, 1, 2, 3, 4, 5].map((i) => (t.row[i] ?? 0) + (row[i] ?? 0)) as DeedRow;
    const best = (d.best ??= {});
    const long = fd.ticks >= RECORD_MIN_TICKS;
    const dps = better(best.dps, long ? perSecond(row[0], fd.ticks) : 0, mode, now);
    const hps = better(best.hps, long ? perSecond(row[5] ?? 0, fd.ticks) : 0, mode, now);
    const taken = better(best.taken, row[2], mode, now);
    const kills = better(best.kills, row[3], mode, now);
    if (dps) best.dps = dps;
    if (hps) best.hps = hps;
    if (taken) best.taken = taken;
    if (kills) best.kills = kills;
  }
};

// ---------- the bastion's half: gathering and building ----------

/** A hero's works, started on first use (only for players of the campaign). */
const worksOf = (s: CampaignState, pid: PlayerId): HeroWorks | null => {
  if (!s.players.some((p) => p.id === pid)) return null;
  const d = ((s.deeds ??= {})[pid] ??= { by: {} });
  return (d.works ??= { gathered: { wood: 0, stone: 0, crystal: 0, dust: 0, iron: 0 }, given: 0, workMin: 0, raised: 0 });
};

/** A run's loot, shared evenly among the heroes who went live (the leader first, who keeps what doesn't divide). */
export const creditGathered = (s: CampaignState, pids: PlayerId[], loot: Resources): void => {
  const who = pids.filter((pid) => s.players.some((p) => p.id === pid));
  if (!who.length) return;
  who.forEach((pid, i) => {
    const w = worksOf(s, pid);
    if (!w) return;
    for (const k of ['wood', 'stone', 'crystal', 'dust', 'iron'] as const) {
      const n = loot[k] ?? 0;
      w.gathered[k] += idiv(n, who.length) + (i === 0 ? n % who.length : 0);
    }
  });
};

/** Materials a hero put into a blueprint or a task (dust doesn't build). */
export const creditGiven = (s: CampaignState, pid: PlayerId, give: Partial<Resources>): void => {
  const n = (give.wood ?? 0) + (give.stone ?? 0) + (give.crystal ?? 0) + (give.iron ?? 0);
  const w = n > 0 ? worksOf(s, pid) : null;
  if (w) w.given += n;
};

/** Minutes a hero worked on site. */
export const creditWork = (s: CampaignState, pid: PlayerId, minutes: number): void => {
  const w = worksOf(s, pid);
  if (w) w.workMin += minutes;
};

/** A finished work, to whoever planned it. */
export const creditRaised = (s: CampaignState, pid: PlayerId): void => {
  const w = worksOf(s, pid);
  if (w) w.raised++;
};

const RES_KEYS = ['wood', 'stone', 'crystal', 'dust', 'iron'] as const;

/** What a command took from the storehouse and put into it (`had`: the storehouse before it), to whoever gave it. */
export const creditStorage = (s: CampaignState, pid: PlayerId, cmd: string, had: Resources): void => {
  for (const k of RES_KEYS) {
    const d = (s.storage[k] ?? 0) - (had[k] ?? 0);
    if (d === 0) continue;
    const w = worksOf(s, pid);
    if (!w) return;
    const into = d < 0 ? (w.spent ??= {}) : (w.got ??= {});
    const row = (into[cmd] ??= {});
    row[k] = (row[k] ?? 0) + (d < 0 ? -d : d);
  }
};
